export interface ManagerNavRoutes {
  users: string;
  teams: string;
  projects: string;
  orgs: string;
  logs: string;
}

export interface ManagerNavItem {
  to: string;
  label: string;
  i18nKey: string;
}

export interface BuildManagerNavItemsInput {
  routes: ManagerNavRoutes;
  /**
   * The user holds a role that may see the management surfaces
   * (Users / Teams / Projects / Orgs) — i.e. one of MANAGER_SURFACE_ROLES.
   * Removing the View Only floor (PSTUDIO-4099) hides these for a role-less
   * user; their own profile (My Account) is a separate, always-reachable
   * surface (PSTUDIO-4095).
   */
  hasManagerAccess: boolean;
  /** The user is an org/super admin — the only role that may see Logs. */
  isAdminRole: boolean;
}

/**
 * Build the visible Manager primary-nav entries from the current access flags.
 * Pure and free of React / design-system imports so the visibility rule is
 * unit-testable (the nav itself renders Lit web components, which jsdom cannot
 * mount).
 */
export const buildManagerNavItems = ({
  routes,
  hasManagerAccess,
  isAdminRole,
}: BuildManagerNavItemsInput): ManagerNavItem[] => {
  const entries: Array<{ item: ManagerNavItem; visible: boolean }> = [
    {
      item: {
        to: routes.users,
        label: 'Users',
        i18nKey: 'manager/common:navMenu.users',
      },
      visible: hasManagerAccess,
    },
    {
      item: {
        to: routes.teams,
        label: 'Teams',
        i18nKey: 'manager/common:navMenu.teams',
      },
      visible: hasManagerAccess,
    },
    {
      item: {
        to: routes.projects,
        label: 'Projects',
        i18nKey: 'manager/common:navMenu.projects',
      },
      visible: hasManagerAccess,
    },
    {
      item: {
        to: routes.orgs,
        label: 'Orgs',
        i18nKey: 'manager/common:navMenu.orgs',
      },
      visible: hasManagerAccess,
    },
    {
      item: {
        to: routes.logs,
        label: 'Logs',
        i18nKey: 'manager/common:navMenu.logs',
      },
      visible: isAdminRole,
    },
  ];

  return entries.filter((entry) => entry.visible).map((entry) => entry.item);
};
